<template>
  <Panel
    :header="t('Auditoria de conversas')"
    class="assistant-config-audit"
    data-testid="audit-panel"
  >
    <p class="assistant-config-help">
      {{ t('Visualização somente leitura de todas as conversas (acesso Full).') }}
    </p>

    <Message
      v-if="chatsError"
      severity="error"
      :closable="false"
      data-testid="audit-chats-error"
    >
      {{ chatsError }}
    </Message>

    <div class="assistant-config-audit-body">
      <!-- Chat list (all owners) -->
      <aside class="assistant-config-audit-list" data-testid="audit-chat-list">
        <p
          v-if="chats.length === 0 && !chatsLoading"
          class="assistant-config-empty"
          data-testid="audit-chat-empty"
        >
          {{ t('Nenhuma conversa encontrada') }}
        </p>
        <ul v-else class="assistant-config-audit-items">
          <li
            v-for="(chat, index) in chats"
            :key="chat.id"
            class="assistant-config-audit-item"
            :class="{ 'assistant-config-audit-item-active': chat.id === activeChatId }"
            :data-testid="`audit-chat-list-item`"
            :data-testid-index="index"
            :data-testid-chat-id="chat.id"
          >
            <button
              type="button"
              class="assistant-config-audit-select"
              data-testid="audit-chat-select"
              @click="openChat(chat.id)"
            >
              <span class="assistant-config-audit-title">{{ chat.title }}</span>
              <span class="assistant-config-audit-owner">{{ chat.userId }}</span>
            </button>
          </li>
        </ul>
      </aside>

      <!-- Selected chat's messages (read-only) -->
      <section class="assistant-config-audit-thread" data-testid="audit-chat-messages">
        <p
          v-if="activeChatId === null"
          class="assistant-config-empty"
          data-testid="audit-chat-unselected"
        >
          {{ t('Selecione uma conversa para ver as mensagens') }}
        </p>

        <ProgressSpinner
          v-else-if="threadLoading"
          style="width: 2rem; height: 2rem"
          data-testid="audit-chat-loading"
        />

        <template v-else>
          <p
            v-if="messages.length === 0"
            class="assistant-config-empty"
            data-testid="audit-chat-no-messages"
          >
            {{ t('Esta conversa não tem mensagens') }}
          </p>

          <div
            v-for="(message, index) in messages"
            :key="message.id"
            class="assistant-config-audit-msg"
            :class="message.role === 'user'
              ? 'assistant-config-audit-msg-user'
              : 'assistant-config-audit-msg-assistant'"
            :data-testid="`audit-message-${index}`"
            :data-testid-role="message.role"
          >
            <span class="assistant-config-audit-msg-role">{{ roleLabel(message.role) }}</span>
            <span class="assistant-config-audit-msg-content">{{ message.content }}</span>
            <AssistantMessageDiagnostics
              v-if="message.role === 'assistant' && message.diagnostics !== null"
              :diagnostics="message.diagnostics"
            />
          </div>
        </template>
      </section>
    </div>
  </Panel>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useChats } from '../../composables/useChats';
import { useAgentChat } from '../../composables/useAgentChat';
import type { ChatMessageRole } from '../../composables/aiAgentContract';

const { t } = useI18n();

const { chats, loading: chatsLoading, error: chatsError, listChats } = useChats();
const {
  messages,
  loading: threadLoading,
  activeChatId,
  loadThread,
} = useAgentChat();

function roleLabel(role: ChatMessageRole): string {
  return role === 'user' ? t('Usuário') : t('Assistente');
}

async function openChat(chatId: number): Promise<void> {
  if (chatId === activeChatId.value) {
    return;
  }
  try {
    await loadThread(chatId);
  } catch {
    // loadThread sets its own error state on the composable; the audit thread
    // simply stays on the previously-open chat. Swallowed so a single failed
    // open does not bubble a global toast.
  }
}

onMounted(async () => {
  try {
    await listChats();
  } catch {
    // The composable already set `chatsError`; matches the original allSettled
    // behaviour so a load rejection never propagates as unhandled.
  }
});
</script>

<style scoped>
.assistant-config-help {
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
}

.assistant-config-audit-body {
  display: flex;
  gap: 1rem;
  align-items: stretch;
}

.assistant-config-audit-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 14rem;
  max-width: 18rem;
  border-right: 1px solid var(--p-surface-200);
  padding-right: 0.75rem;
  overflow-y: auto;
  max-height: 24rem;
}

.assistant-config-audit-items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.assistant-config-audit-item {
  border-radius: 0.5rem;
}

.assistant-config-audit-item-active {
  background: var(--p-surface-200);
}

.assistant-config-audit-select {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  text-align: left;
  color: var(--p-text-color);
  font: inherit;
}

.assistant-config-audit-title {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.assistant-config-audit-owner {
  font-size: 0.7rem;
  color: var(--p-text-muted-color);
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.assistant-config-audit-thread {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow-y: auto;
  max-height: 24rem;
  padding: 0.25rem;
}

.assistant-config-audit-msg {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: var(--p-surface-100);
}

.assistant-config-audit-msg-assistant {
  background: var(--p-surface-200);
}

.assistant-config-audit-msg-role {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--p-text-muted-color);
}

.assistant-config-audit-msg-content {
  white-space: pre-wrap;
  word-break: break-word;
}

.assistant-config-empty {
  color: var(--p-text-muted-color);
  text-align: center;
  font-size: 0.85rem;
  margin: 1rem 0;
}
</style>
